import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Factory, ExternalLink, MessageCircleQuestion, CheckCircle2, Clock,
  BadgeCheck, ArrowLeft, Send,
} from "lucide-react";
import { useAuth } from "../AuthContext";
import { API_URL } from "../config";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("ru", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}

function AskForm({ slug, onAsked, onLoginRequest }) {
  const { isLoggedIn, token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [deviceModel, setDeviceModel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/manufacturers/${slug}/questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: title.trim(), text: text.trim(),
          device_model: deviceModel.trim() || null,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Не удалось отправить вопрос");
      setTitle(""); setText(""); setDeviceModel(""); setOpen(false);
      onAsked();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <button onClick={onLoginRequest} style={{
        background: "#e5005b", color: "#fff", border: "none", borderRadius: 8,
        padding: "9px 18px", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", marginTop: 0,
      }}>
        Войти, чтобы задать вопрос
      </button>
    );
  }

  if (user && !user.email_verified) {
    return (
      <div style={{ background: "#fff1f6", border: "1px solid #ffc2d9", borderRadius: 8, padding: "10px 14px", color: "#9c0040", fontSize: "0.88rem" }}>
        Подтвердите email, чтобы задавать вопросы производителю.
      </div>
    );
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={{
        background: "#e5005b", color: "#fff", border: "none", borderRadius: 8,
        padding: "9px 18px", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", marginTop: 0,
        display: "inline-flex", alignItems: "center", gap: 6,
      }}>
        <MessageCircleQuestion size={16} strokeWidth={2.2} /> Задать вопрос
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="card" style={{ marginBottom: 0 }}>
      {error && <div className="auth-error" style={{ marginBottom: 10 }}>{error}</div>}
      <label style={{ display: "block", fontSize: "0.85rem", color: "#555", marginBottom: 10 }}>
        Суть вопроса
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} required maxLength={200}
          placeholder="Например: экспорт нативной записи Холтера"
          style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 8, border: "1px solid #c4ceda", boxSizing: "border-box" }} />
      </label>
      <label style={{ display: "block", fontSize: "0.85rem", color: "#555", marginBottom: 10 }}>
        Модель прибора (необязательно)
        <input type="text" value={deviceModel} onChange={e => setDeviceModel(e.target.value)} maxLength={150}
          placeholder="КТ-07, CardioMera, MT-101…"
          style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 8, border: "1px solid #c4ceda", boxSizing: "border-box" }} />
      </label>
      <label style={{ display: "block", fontSize: "0.85rem", color: "#555", marginBottom: 12 }}>
        Подробности
        <textarea value={text} onChange={e => setText(e.target.value)} required rows={4} maxLength={5000}
          placeholder="Что именно не получается, что уже пробовали"
          style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 8, border: "1px solid #c4ceda", boxSizing: "border-box" }} />
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={busy} style={{
          background: "#e5005b", color: "#fff", border: "none", borderRadius: 8,
          padding: "8px 18px", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", marginTop: 0,
          display: "inline-flex", alignItems: "center", gap: 6,
        }}>
          <Send size={14} strokeWidth={2.2} /> {busy ? "Отправляем…" : "Отправить вопрос"}
        </button>
        <button type="button" onClick={() => setOpen(false)} style={{
          background: "none", border: "none", color: "#888", fontWeight: 600,
          fontSize: "0.88rem", cursor: "pointer", marginTop: 0,
        }}>
          Отмена
        </button>
      </div>
    </form>
  );
}

function QuestionCard({ question, isRep, token, onAnswered }) {
  const [expanded, setExpanded] = useState(false);
  const [full, setFull] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [busy, setBusy] = useState(false);

  const loadFull = useCallback(() => {
    fetch(`${API_URL}/manufacturer-questions/${question.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(setFull)
      .catch(() => {});
  }, [question.id]);

  const toggle = () => {
    if (!expanded && !full) loadFull();
    setExpanded(e => !e);
  };

  const submitAnswer = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/manufacturer-questions/${question.id}/answers`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: answerText.trim() }),
      });
      if (r.ok) {
        setFull(await r.json());
        setAnswerText("");
        onAnswered();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div onClick={toggle} style={{ cursor: "pointer" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 700, color: "#1f2937", fontSize: "0.98rem" }}>{question.title}</div>
          {question.is_answered ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#15803d", fontSize: "0.8rem", fontWeight: 700, whiteSpace: "nowrap" }}>
              <CheckCircle2 size={14} strokeWidth={2.4} /> Есть ответ
            </span>
          ) : (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#c4004f", fontSize: "0.8rem", fontWeight: 700, whiteSpace: "nowrap" }}>
              <Clock size={14} strokeWidth={2.4} /> Ждёт ответа
            </span>
          )}
        </div>
        <div style={{ color: "#888", fontSize: "0.82rem", marginTop: 3 }}>
          {question.author_name}
          {question.device_model && <> · прибор: {question.device_model}</>}
          {" · "}{fmtDate(question.created_at)}
          {question.answers_count > 0 && <> · ответов: {question.answers_count}</>}
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #eef0f6" }}>
          {!full ? (
            <p style={{ color: "#888", fontSize: "0.88rem" }}>Загрузка…</p>
          ) : (
            <>
              <p style={{ color: "#374151", fontSize: "0.92rem", whiteSpace: "pre-wrap", marginTop: 0 }}>{full.text}</p>

              {full.answers.map(a => (
                <div key={a.id} style={{
                  marginTop: 10, padding: "10px 14px", borderRadius: 8,
                  background: a.is_official ? "#f0fdfa" : "#f7f8fc",
                  border: a.is_official ? "1px solid #99f6e4" : "1px solid #e2e4f0",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#1f2937" }}>{a.author_name}</span>
                    {a.is_official && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 3, background: "#15803d", color: "#fff", borderRadius: 5, padding: "1px 8px", fontSize: "0.72rem", fontWeight: 700 }}>
                        <BadgeCheck size={11} strokeWidth={2.6} /> представитель
                      </span>
                    )}
                    {a.author_job_title && (
                      <span style={{ color: "#888", fontSize: "0.78rem" }}>{a.author_job_title}</span>
                    )}
                    <span style={{ color: "#aaa", fontSize: "0.78rem" }}>{fmtDate(a.created_at)}</span>
                  </div>
                  <div style={{ color: "#374151", fontSize: "0.9rem", whiteSpace: "pre-wrap" }}>{a.text}</div>
                </div>
              ))}

              {isRep && (
                <form onSubmit={submitAnswer} style={{ marginTop: 12 }}>
                  <textarea value={answerText} onChange={e => setAnswerText(e.target.value)} required rows={3} maxLength={5000}
                    placeholder="Ответ от имени компании"
                    style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #99f6e4", fontSize: "0.9rem", boxSizing: "border-box" }} />
                  <button type="submit" disabled={busy} style={{
                    marginTop: 6, background: "#15803d", color: "#fff", border: "none", borderRadius: 8,
                    padding: "7px 16px", fontWeight: 700, fontSize: "0.86rem", cursor: "pointer",
                  }}>
                    {busy ? "Отправляем…" : "Ответить"}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ManufacturerDetail({ onLoginRequest }) {
  const { slug } = useParams();
  const { token, user } = useAuth();
  const [manufacturer, setManufacturer] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [filter, setFilter] = useState("all");

  const loadQuestions = useCallback(() => {
    const params = filter === "all" ? "" : `?answered=${filter === "answered"}`;
    fetch(`${API_URL}/manufacturers/${slug}/questions${params}`)
      .then(r => r.ok ? r.json() : [])
      .then(setQuestions)
      .catch(() => setQuestions([]));
  }, [slug, filter]);

  useEffect(() => {
    setNotFound(false);
    fetch(`${API_URL}/manufacturers/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setManufacturer)
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => { loadQuestions(); }, [loadQuestions]);

  if (notFound) return <section><p style={{ color: "#888" }}>Производитель не найден.</p></section>;
  if (!manufacturer) return <section><p style={{ color: "#888" }}>Загрузка…</p></section>;

  const isRep = !!user && (
    user.role === "admin" || manufacturer.reps.some(r => r.user_id === user.id)
  );

  const tabStyle = active => ({
    padding: "6px 14px", borderRadius: 8,
    border: "1.5px solid " + (active ? "#e5005b" : "#e5e7eb"),
    background: active ? "#e5005b" : "#fff", color: active ? "#fff" : "#4b5563",
    fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", marginTop: 0,
  });

  return (
    <section style={{ maxWidth: 820 }}>
      <Link to="/manufacturers" style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#e5005b", fontSize: "0.88rem", fontWeight: 600, textDecoration: "none", marginBottom: 14 }}>
        <ArrowLeft size={15} /> Все производители
      </Link>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          {manufacturer.logo_url ? (
            <img src={manufacturer.logo_url} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: "contain", flexShrink: 0, background: "#fff" }} />
          ) : (
            <div style={{
              width: 64, height: 64, borderRadius: 10, background: "#eef0f8", color: "#007c84",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Factory size={28} strokeWidth={2} />
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <h2 className="section-title" style={{ margin: 0 }}>{manufacturer.name}</h2>
            {manufacturer.website && (
              <a href={manufacturer.website} target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#e5005b", fontSize: "0.85rem", marginTop: 4 }}>
                {manufacturer.website.replace(/^https?:\/\//, "")} <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>

        {manufacturer.description && (
          <p style={{ color: "#374151", fontSize: "0.94rem", lineHeight: 1.6, marginTop: 14, marginBottom: 0 }}>
            {manufacturer.description}
          </p>
        )}

        {manufacturer.reps.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #eef0f6" }}>
            <div style={{ fontSize: "0.76rem", color: "#888", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>
              {manufacturer.reps.length > 1 ? "Представители компании" : "Представитель компании"}
            </div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {manufacturer.reps.map(r => (
                <div key={r.user_id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {r.avatar_url ? (
                    <img src={r.avatar_url} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#15803d", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "0.9rem" }}>
                      {(r.display_name || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#1f2937" }}>{r.display_name}</div>
                    {r.job_title && <div style={{ color: "#888", fontSize: "0.78rem" }}>{r.job_title}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <button style={tabStyle(filter === "all")} onClick={() => setFilter("all")}>Все вопросы</button>
        <button style={tabStyle(filter === "unanswered")} onClick={() => setFilter("unanswered")}>Ждут ответа</button>
        <button style={tabStyle(filter === "answered")} onClick={() => setFilter("answered")}>С ответом</button>
      </div>

      {/* Форма — отдельным блоком: в раскрытом виде это полноширинная карточка,
          внутри строки с фильтрами она была бы зажата. */}
      <div style={{ marginBottom: 18 }}>
        <AskForm slug={slug} onAsked={loadQuestions} onLoginRequest={onLoginRequest} />
      </div>

      {questions.length === 0 ? (
        <p style={{ color: "#888" }}>
          {filter === "all" ? "Вопросов пока нет — задайте первый." : "В этой категории пусто."}
        </p>
      ) : (
        questions.map(q => (
          <QuestionCard key={q.id} question={q} isRep={isRep} token={token} onAnswered={loadQuestions} />
        ))
      )}
    </section>
  );
}
